import {
	BankAuthenticationSuccessData,
	ChooseAccountData,
	ChooseBankData,
	FormData,
	ISessionService,
	SessionConfiguration
} from './interfaces/session.interface';
import { Session, SessionState } from '../types/session';
import { JWK } from 'jose';
import axios, { AxiosResponse } from 'axios';
import { FincredibleSession } from '../types/bank-integration-vendor';
import { client } from '../api/client';
import { encrypt } from '../utils/encryption';
import { Account } from '../types/account';

const SESSION_OBJECT: Session = {
	sessionId: 'FINCREDIBLE_SESSION',
	vendor: '',
	token: ''
};

export class FincredibleSessionService implements ISessionService {
	async createSession(configuration: SessionConfiguration): Promise<Session> {
		return SESSION_OBJECT;
	}

	async getSession(): Promise<Session> {
		return SESSION_OBJECT;
	}

	async getSessionState(sessionId: string): Promise<SessionState | undefined> {
		try {
			const response: AxiosResponse<FincredibleSession> = await client.get('v2/bank/session');
			return mapFincredibleSessionToSessionState(response.data);
		} catch (error) {
			if (axios.isAxiosError(error) && error.response?.status === 404) {
				return undefined;
			}
			throw error;
		}
	}

	async authenticated(sessionId: string, data: BankAuthenticationSuccessData): Promise<SessionState> {
		const response: AxiosResponse<FincredibleSession> = await client.put('v2/bank/session/redirect', data);
		return mapFincredibleSessionToSessionState(response.data);
	}

	async chooseBank(sessionId: string, data: ChooseBankData): Promise<SessionState> {
		const response: AxiosResponse<FincredibleSession> = await client.post('v2/bank/session', {
			bankId: Number(data.bankId)
		});
		return mapFincredibleSessionToSessionState(response.data);
	}

	async chooseAccount(sessionId: string, data: ChooseAccountData): Promise<SessionState> {
		const response: AxiosResponse<FincredibleSession> = await client.put(
			`/v2/bank/session/account/${data.accountId}`
		);
		return mapFincredibleSessionToSessionState(response.data);
	}

	async submitForm(sessionId: string, formId: string, data: FormData, key: JWK): Promise<SessionState> {
		const encrypted = await encrypt(JSON.stringify(data), key);
		const response: AxiosResponse<FincredibleSession> = await client.put('v2/bank/session/form', {
			form: encrypted
		});
		return mapFincredibleSessionToSessionState(response.data);
	}

	async logEvent(): Promise<void> {}
}

function mapFincredibleSessionToSessionState(session: FincredibleSession): SessionState {
	if (session.status === 'ACCOUNT_SELECT') {
		return {
			status: 'CONSUMER_INPUT_NEEDED',
			polling: session.polling,
			result: {
				type: 'account_selection',
				accounts: session.accounts.map((account) => {
					return {
						accountNumber: account.accountNumber,
						id: account.id,
						alias: account.alias,
						holderName: account.holderName,
						iban: account.iban,
						accountType: account.accountType
					} as Account;
				})
			}
		};
	}
	if (session.status === 'AUTH_FORM') {
		return {
			polling: session.polling || false,
			status: 'CONSUMER_INPUT_NEEDED',
			result: {
				type: 'form',
				form: session.schema,
				key: session.key,
				context: session.context || ''
			}
		};
	}
	if (session.status === 'AUTH_REDIRECT') {
		return {
			polling: session.polling || false,
			status: 'CONSUMER_INPUT_NEEDED',
			result: {
				type: 'redirect',
				url: session.url
			}
		};
	}
	if (session.status === 'FINISHED') {
		return {
			polling: session.polling || false,
			status: 'FINISHED'
		};
	}
	if (session.status === 'PROCESSING') {
		return {
			polling: session.polling || false,
			status: 'PROCESSING'
		};
	}
	if (session.status === 'CANCELED') {
		return {
			status: 'ABORTED',
			polling: false
		};
	}
	return {
		status: 'EXCEPTION',
		result: {
			type: 'error',
			error: { category: 'INTERNAL_SERVER_ERROR', message: '' }
		}
	};
}

export default new FincredibleSessionService();
